<template>
  <div class="nvx-role-dropdown" v-click-outside="close">
    <button
      class="nvx-role-dropdown__trigger"
      @click.stop="handleClick"
      :class="{
        'nvx-role-dropdown__trigger--active': isOpen,
        'nvx-role-dropdown__trigger--admin': selectedRole === 'Admin',
        'nvx-role-dropdown__trigger--editor': selectedRole === 'Editor',
        'nvx-role-dropdown__trigger--profile': selectedRole === 'Profile',
        'nvx-role-dropdown__trigger--disabled': !canSwitch
      }"
      :disabled="!canSwitch"
    >
      <span class="nvx-role-dropdown__role-text">{{ selectedRole }}</span>
      <NvxIcon
        v-if="canSwitch"
        name="chevron-down"
        size="sm"
        class="nvx-role-dropdown__chevron"
        :class="{ 'nvx-role-dropdown__chevron--rotated': isOpen }"
      />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="nvx-role-dropdown__content"
        :style="dropdownStyles"
      >
        <div
          v-for="role in availableRoles"
          :key="role.value"
          class="nvx-role-dropdown__item"
          :class="{ 'nvx-role-dropdown__item--selected': role.value === selectedRole }"
          @click="selectRole(role.value)"
        >
          <span class="nvx-role-dropdown__item-text">{{ role.label }}</span>
          <NvxIcon 
            v-if="role.value === selectedRole" 
            name="check" 
            size="sm" 
            class="nvx-role-dropdown__check-icon"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import NvxIcon from './NvxIcon.vue'

interface Role {
  value: string
  label: string
}

interface Props {
  modelValue?: string
  availableRoles?: Role[]
  canSwitch?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 'Admin',
  availableRoles: () => [
    { value: 'Admin', label: 'Admin' },
    { value: 'Editor', label: 'Editor' }
  ],
  canSwitch: true
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  change: [role: string]
}>()

const isOpen = ref(false)
const dropdownPosition = ref({ top: 0, left: 0 })

const selectedRole = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const dropdownStyles = computed(() => ({
  position: 'fixed' as const,
  top: `${dropdownPosition.value.top}px`,
  left: `${dropdownPosition.value.left}px`,
  zIndex: 9999
}))

const handleClick = (event: MouseEvent) => {
  // Don't open dropdown if switching is disabled
  if (!props.canSwitch) return

  if (!isOpen.value) {
    updateDropdownPosition(event)
  }
  isOpen.value = !isOpen.value
}

const close = () => {
  isOpen.value = false
}

const selectRole = (role: string) => {
  selectedRole.value = role
  emit('change', role)
  close()
}

const updateDropdownPosition = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const rect = target.getBoundingClientRect()
  
  dropdownPosition.value = {
    top: rect.bottom + 4,
    left: rect.left
  }
}

// Click outside directive
interface HTMLElementWithClickOutside extends HTMLElement {
  clickOutsideEvent?: (event: Event) => void
}

const vClickOutside = {
  mounted(el: HTMLElementWithClickOutside, binding: any) {
    el.clickOutsideEvent = (event: Event) => {
      if (!(el === event.target || el.contains(event.target as Node))) {
        binding.value()
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el: HTMLElementWithClickOutside) {
    if (el.clickOutsideEvent) {
      document.removeEventListener('click', el.clickOutsideEvent)
    }
  }
}
</script>

<style lang="scss" scoped>
.nvx-role-dropdown {
  position: relative;

  &__trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--nvx-spacing-2);
    border: none;
    border-radius: var(--nvx-border-radius-2xl);
    color: white;
    font-size: var(--nvx-font-size-sm);
    font-weight: var(--nvx-font-weight-medium);
    padding: var(--nvx-spacing-2) var(--nvx-spacing-3);
    cursor: pointer;
    transition: all 0.2s ease;
    width: 76px;
    height: 20px;

    // Admin role styling (default)
    &--admin {
      background-color: #04434A;
    }

    // Editor role styling
    &--editor {
      background-color: #5A30B7;
    }

    // Profile role styling
    &--profile {
      background-color: #27007A;
      border: 1px solid #7E40FF;
    }

    // Disabled state - no hover effects, no pointer cursor
    &--disabled {
      cursor: default;
      pointer-events: none;

      &:hover {
        // Keep same background, no hover effect
      }
    }
  }

  &__role-text {
    text-align: center;
  }

  &__chevron {
    transition: transform 0.2s ease;
    color: white;

    &--rotated {
      transform: rotate(180deg);
    }
  }

  &__content {
    background-color: #15253C;
    border: 1px solid #25416A;
    border-radius: var(--nvx-border-radius-lg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    min-width: 120px;
    overflow: hidden;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--nvx-spacing-3) var(--nvx-spacing-4);
    cursor: pointer;
    transition: background-color 0.2s ease;
    color: var(--nvx-color-text-primary);

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }

    &--selected {
      background-color: rgba(126, 64, 255, 0.1);
    }
  }

  &__item-text {
    font-size: var(--nvx-font-size-sm);
    font-weight: var(--nvx-font-weight-medium);
  }

  &__check-icon {
    color: var(--nvx-color-accent-primary);
  }
}
</style>
